// Operator screen styling. Built from src/design tokens only (brand blue,
// ink, hairlines, panel elevation) and the SENTIMENT ramp for status, the
// same vocabulary the health check page uses, so nothing here is a new colour.
import styled, { css, keyframes } from "styled-components";
import {
  FONT, FIG_INK, FIG_MUTED, PANEL_BORDER, CARD_BORDER, HAIRLINE, TRACK,
  SHADOW_PANEL, RADIUS_PANEL, RADIUS_CTA, PRIMARY, PRIMARY_SOFT, SENTIMENT,
} from "../../design";

export const STATUS = {
  running: { fg: PRIMARY, bg: "rgba(46, 69, 218, 0.10)", label: "Running" },
  queued: { fg: FIG_MUTED, bg: "rgba(109, 106, 103, 0.08)", label: "Queued" },
  done: { fg: SENTIMENT.POSITIVE, bg: "rgba(46, 125, 107, 0.10)", label: "Done" },
  failed: { fg: SENTIMENT.NEGATIVE, bg: "rgba(177, 75, 62, 0.10)", label: "Failed" },
  waiting: { fg: SENTIMENT.AMBER, bg: "rgba(217, 119, 6, 0.10)", label: "Waiting for approval" },
  simulated: { fg: "#5B6577", bg: "rgba(154, 164, 179, 0.18)", label: "Simulated" },
};

export const RISK = {
  none: { fg: FIG_MUTED, bg: "rgba(109, 106, 103, 0.08)", label: "Read only" },
  platform: { fg: PRIMARY, bg: "rgba(46, 69, 218, 0.08)", label: "Platform" },
  external: { fg: SENTIMENT.AMBER, bg: "rgba(217, 119, 6, 0.10)", label: "External" },
  money: { fg: SENTIMENT.AMBER, bg: "rgba(217, 119, 6, 0.14)", label: "Spends money" },
  irreversible: { fg: SENTIMENT.NEGATIVE, bg: "rgba(177, 75, 62, 0.10)", label: "Cannot be undone" },
};

export const Page = styled.div`
  font-family: ${FONT};
  color: ${FIG_INK};
  /* The platform gutter from the navigation (24px), like the other pages. */
  padding: 16px 24px 0;
  height: calc(100vh - 36px);
  min-height: 560px;
  display: grid;
  grid-template-rows: auto 1fr;
  /* One column that may shrink below its content's width: without this a
     long conversation title (kept on one line) widens the page past the
     screen. */
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  box-sizing: border-box;
  @media (max-width: 1100px) {
    height: auto;
  }
`;

export const TopBar = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  min-width: 0;
  > button, > a { flex: 0 0 auto; white-space: nowrap; }
`;

export const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${FIG_INK};
`;

export const Subtle = styled.span`
  font-size: 12px;
  color: ${FIG_MUTED};
`;

export const Spacer = styled.div`
  flex: 1;
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(380px, 480px);
  gap: 16px;
  min-height: 0;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const panel = css`
  background: #fff;
  border: 1px solid ${PANEL_BORDER};
  border-radius: ${RADIUS_PANEL}px;
  box-shadow: ${SHADOW_PANEL};
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const ChatPanel = styled.section`
  ${panel}
  @media (max-width: 1100px) {
    height: 70vh;
  }
`;

export const FeedPanel = styled.aside`
  ${panel}
  @media (max-width: 1100px) {
    height: 60vh;
  }
`;

export const PanelHeader = styled.div`
  padding: 14px 20px 12px;
  border-bottom: 1px solid ${HAIRLINE};
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  h2 {
    font-size: 14px;
    font-weight: 700;
    margin: 0;
  }
`;

export const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 20px;
  min-height: 0;
`;

export const MessageRow = styled.div`
  display: flex;
  justify-content: ${(p) => (p.$user ? "flex-end" : "flex-start")};
  margin-bottom: 16px;
`;

export const MsgCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$user ? "flex-end" : "flex-start")};
  max-width: ${(p) => (p.$user ? "78%" : "100%")};
  width: ${(p) => (p.$user ? "auto" : "100%")};
`;

export const Stamp = styled.span`
  font-size: 11px;
  color: ${FIG_MUTED};
  margin-top: 4px;
  padding: 0 4px;
`;

export const BackLink = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${PRIMARY};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;

export const ChatTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 0 8px;
  color: ${FIG_INK};
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SimTag = styled.span`
  display: inline-block;
  margin-bottom: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #5B6577;
  background: rgba(154, 164, 179, 0.18);
`;

export const Bubble = styled.div`
  max-width: 100%;
  font-size: 14px;
  line-height: 1.6;
  ${(p) =>
    p.$user
      ? css`
          background: ${PRIMARY_SOFT};
          color: ${FIG_INK};
          padding: 10px 14px;
          border-radius: 14px 14px 4px 14px;
          white-space: pre-wrap;
        `
      : css`
          color: ${FIG_INK};
          padding: 2px 2px;
          width: 100%;
        `}
`;

export const ErrorNote = styled.div`
  font-size: 13px;
  color: ${SENTIMENT.NEGATIVE};
  background: rgba(177, 75, 62, 0.08);
  border-radius: 10px;
  padding: 8px 12px;
  margin-top: 6px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;

export const Working = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${FIG_MUTED};
  margin: 4px 0 12px;
  span.dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${PRIMARY};
    animation: ${pulse} 1.2s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    span.dot { animation: none; }
  }
`;

export const InterimList = styled.ul`
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  li {
    font-size: 12.5px;
    color: ${FIG_MUTED};
    line-height: 1.5;
    padding: 2px 0;
  }
`;

export const ComposerWrap = styled.div`
  padding: 12px 16px 14px;
  border-top: 1px solid ${HAIRLINE};
  display: flex;
`;

export const Empty = styled.div`
  max-width: 560px;
  margin: 8vh auto 0;
  text-align: center;
  h3 {
    font-size: 17px;
    font-weight: 700;
    margin: 0 0 6px;
  }
  p {
    font-size: 13px;
    color: ${FIG_MUTED};
    margin: 0 0 16px;
  }
`;

export const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  button {
    font-family: ${FONT};
    font-size: 12.5px;
    color: ${FIG_INK};
    background: #fff;
    border: 1px solid ${CARD_BORDER};
    border-radius: 999px;
    padding: 7px 12px;
    cursor: pointer;
    text-align: left;
    &:hover {
      border-color: ${PRIMARY};
      color: ${PRIMARY};
    }
    &:focus-visible {
      outline: 2px solid ${PRIMARY};
      outline-offset: 2px;
    }
  }
`;

export const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(p) => p.$fg};
  background: ${(p) => p.$bg};
`;

/* ── Activity feed ─────────────────────────────────────────────────── */

export const TurnBlock = styled.div`
  margin-bottom: 14px;
`;

export const TurnHead = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${FIG_MUTED};
  padding: 4px 0 8px;
  &:focus-visible {
    outline: 2px solid ${PRIMARY};
    outline-offset: 2px;
  }
  span.q {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
`;

export const Group = styled.div`
  border: 1px solid ${CARD_BORDER};
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #fff;
`;

export const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .role {
    font-size: 13px;
    font-weight: 700;
  }
  .title {
    font-size: 12.5px;
    color: ${FIG_MUTED};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Steps = styled.ol`
  list-style: none;
  margin: 8px 0 0;
  padding: 0 0 0 4px;
`;

export const Step = styled.li`
  position: relative;
  padding: 4px 0 4px 18px;
  font-size: 12.5px;
  line-height: 1.45;
  &::before {
    content: "";
    position: absolute;
    left: 3px;
    top: 10px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => (STATUS[p.$status] || STATUS.done).fg};
  }
  &:not(:last-child)::after {
    content: "";
    position: absolute;
    left: 6px;
    top: 19px;
    bottom: -6px;
    width: 1px;
    background: ${TRACK};
  }
  .detail {
    color: ${FIG_MUTED};
    font-size: 12px;
  }
  .sources {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 3px;
  }
  .sources a {
    font-size: 11px;
    color: ${PRIMARY};
    background: rgba(46, 69, 218, 0.06);
    border-radius: 6px;
    padding: 1px 6px;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

export const FeedEmpty = styled.p`
  font-size: 12.5px;
  color: ${FIG_MUTED};
  margin: 0;
`;

/* ── Plan card ─────────────────────────────────────────────────────── */

export const PlanCardShell = styled.div`
  border: 1px solid ${CARD_BORDER};
  border-radius: 14px;
  margin: 10px 0 4px;
  overflow: hidden;
  background: #fff;
`;

export const PlanHead = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${HAIRLINE};
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  .t {
    font-size: 14px;
    font-weight: 700;
  }
  .s {
    font-size: 12.5px;
    color: ${FIG_MUTED};
    flex-basis: 100%;
  }
`;

export const PlanTable = styled.div`
  overflow-x: auto;
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
  }
  th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${FIG_MUTED};
    padding: 8px 14px;
    border-bottom: 1px solid ${HAIRLINE};
    white-space: nowrap;
  }
  td {
    padding: 8px 14px;
    border-bottom: 1px solid ${HAIRLINE};
    vertical-align: top;
  }
  tr:last-child td { border-bottom: none; }
  .why {
    color: ${FIG_MUTED};
    font-size: 12px;
    margin-top: 2px;
  }
  .num {
    font-variant-numeric: tabular-nums;
    color: ${FIG_MUTED};
  }
`;

export const PlanFoot = styled.div`
  padding: 10px 14px 12px;
  border-top: 1px solid ${HAIRLINE};
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  .note {
    font-size: 12px;
    color: ${FIG_MUTED};
    flex: 1;
    min-width: 200px;
  }
`;

export const RunSummary = styled.div`
  padding: 10px 14px;
  background: rgba(46, 125, 107, 0.07);
  border-bottom: 1px solid ${HAIRLINE};
  font-size: 12.5px;
  color: ${FIG_INK};
  line-height: 1.5;
`;

export const Outcome = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: ${FIG_INK};
  span:last-child { line-height: 1.45; }
`;

export const Issues = styled.ul`
  margin: 0;
  padding: 8px 14px 8px 30px;
  background: rgba(217, 119, 6, 0.06);
  font-size: 12px;
  color: ${FIG_INK};
`;

export const GhostButton = styled.button`
  font-family: ${FONT};
  font-size: 13px;
  padding: 8px 14px;
  border-radius: ${RADIUS_CTA}px;
  border: 1px solid ${CARD_BORDER};
  background: #fff;
  color: ${FIG_INK};
  cursor: pointer;
  &:hover:not(:disabled) { border-color: ${FIG_MUTED}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
`;
